import { BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { UNIVERSAL_TIER_CONFIG } from "@/lib/badgeUtils";

export interface TrustBadgeProps {
  trustScore: number;
  trustTier: "newbie" | "reliable" | "trusted" | "elite";
  size?: "xs" | "sm" | "md" | "lg";
  showScore?: boolean;
  showBar?: boolean;
  showLabel?: boolean;
  className?: string;
  onClick?: () => void;
}

// Trusted Critic Badge - Composite Trust Score Tiers
// All tiers use BadgeCheck icon per gamification spec
// Score = (Trustworthy 30%) + (Helpful 30%) + (Reviews 25%) + (Followers 15%)
// Uses UNIVERSAL_TIER_CONFIG for consistent tier colors across all badges
export const TIER_CONFIG = {
  newbie: {
    label: "New Voice",
    icon: BadgeCheck,
    color: UNIVERSAL_TIER_CONFIG.tier1.textColor,
    bgColor: UNIVERSAL_TIER_CONFIG.tier1.badgeGradient,
    barColor: UNIVERSAL_TIER_CONFIG.tier1.barGradient,
    range: [0, 74],
    description: "Building reputation",
  },
  reliable: {
    label: "Reliable Reviewer",
    icon: BadgeCheck,
    color: UNIVERSAL_TIER_CONFIG.tier2.textColor,
    bgColor: UNIVERSAL_TIER_CONFIG.tier2.badgeGradient,
    barColor: UNIVERSAL_TIER_CONFIG.tier2.barGradient,
    range: [75, 84],
    description: "Trusted member",
  },
  trusted: {
    label: "Trusted Foodie",
    icon: BadgeCheck,
    color: UNIVERSAL_TIER_CONFIG.tier3.textColor,
    bgColor: UNIVERSAL_TIER_CONFIG.tier3.badgeGradient,
    barColor: UNIVERSAL_TIER_CONFIG.tier3.barGradient,
    range: [85, 94],
    description: "Highly credible",
  },
  elite: {
    label: "Elite Taster",
    icon: BadgeCheck,
    color: UNIVERSAL_TIER_CONFIG.tier4.textColor,
    bgColor: UNIVERSAL_TIER_CONFIG.tier4.badgeGradient,
    barColor: UNIVERSAL_TIER_CONFIG.tier4.barGradient,
    range: [95, 100],
    description: "Community leader",
  },
} as const;

export default function TrustBadge({
  trustScore,
  trustTier,
  size = "md",
  showScore = true,
  showBar = false,
  showLabel = false,
  className,
  onClick,
}: TrustBadgeProps) {
  const config = TIER_CONFIG[trustTier];
  const Icon = config.icon;

  // Calculate progress within current tier
  const [min, max] = config.range;
  const tierProgress = ((trustScore - min) / (max - min)) * 100;

  const sizes = {
    xs: {
      container: "h-4 w-4",
      icon: "w-2.5 h-2.5",
      text: "text-[8px]",
      barHeight: "h-0.5",
      labelText: "text-[9px]",
    },
    sm: {
      container: "h-5 w-5",
      icon: "w-3 h-3",
      text: "text-[10px]",
      barHeight: "h-0.5",
      labelText: "text-[10px]",
    },
    md: {
      container: "h-7 w-7",
      icon: "w-4 h-4",
      text: "text-xs",
      barHeight: "h-1",
      labelText: "text-xs",
    },
    lg: {
      container: "h-9 w-9",
      icon: "w-5 h-5",
      text: "text-sm",
      barHeight: "h-1",
      labelText: "text-sm",
    },
  };

  const sizeConfig = sizes[size];

  return (
    <div
      className={cn(
        "flex items-center gap-1.5",
        onClick && "cursor-pointer hover-elevate active-elevate-2",
        className
      )}
      data-testid="trust-badge"
      onClick={onClick}
    >
      {/* Badge Icon */}
      <div
        className={cn(
          "rounded-full flex items-center justify-center relative shadow-sm",
          sizeConfig.container,
          config.bgColor
        )}
        data-testid={`trust-badge-${trustTier}`}
      >
        <Icon className={cn(sizeConfig.icon, config.color)} />
        {showScore && (
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 rounded-full px-1 font-semibold ring-2 ring-background",
              config.bgColor,
              config.color,
              sizeConfig.text
            )}
          >
            {trustScore}
          </div>
        )}
      </div>

      {/* Label (optional) */}
      {showLabel && (
        <span
          className={cn(
            "font-semibold whitespace-nowrap",
            config.color,
            sizeConfig.labelText
          )}
        >
          {config.label}
        </span>
      )}

      {/* Trust Bar (optional) */}
      {showBar && (
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "w-full bg-muted rounded-full overflow-hidden",
              sizeConfig.barHeight
            )}
          >
            <div
              className={cn(
                "h-full transition-all duration-300",
                config.barColor
              )}
              style={{ width: `${Math.max(0, Math.min(100, tierProgress))}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to determine tier from score
export function getTrustTier(
  score: number
): "newbie" | "reliable" | "trusted" | "elite" {
  if (score >= 95) return "elite";
  if (score >= 85) return "trusted";
  if (score >= 75) return "reliable";
  return "newbie";
}

// Helper to get tier perks description
export function getTierPerks(
  tier: "newbie" | "reliable" | "trusted" | "elite"
): string[] {
  const perks = {
    newbie: [
      "Build your reputation",
      "Write helpful reviews",
      "Connect with community",
    ],
    reliable: [
      "Trusted by the community",
      "Your reviews help others",
      "Increased visibility",
    ],
    trusted: [
      "Your reviews appear first",
      "Recognized food expert",
      "Priority support",
    ],
    elite: [
      "2× Kudoz on reviews",
      "Early access to promos",
      "Maximum visibility",
      "VIP perks",
    ],
  };
  return perks[tier];
}
